import {Action, State, StateContext} from '@ngxs/store';
import {Injectable} from '@angular/core';
import {SubscriptionService} from '@services/subscription/subscription.service';
import {Router} from '@angular/router';
import {LocalizeRouterService} from '@gilsdav/ngx-translate-router';
import {StripeService} from 'ngx-stripe';
import {ErrorService} from '@services/error/error.service';
import {Observable, of} from 'rxjs';
import {catchError, map, mergeMap, tap} from 'rxjs/operators';
import {IStripePlan} from '@interfaces/subscription-plan';
import {IPaymentMethod} from '@interfaces/payment-method';
import {IInvoice} from '@interfaces/Invoice';
import {
  AddPaymentMethod, ConfirmPayment,
  CreateSubscription, CreateSubscriptionIntent, DeletePaymentMethod,
  GetDefaultPaymentMethod, GetPaymentIntent, GetPaymentMethod,
  GetPaymentMethods,
  GetStripeInvoice, GetStripePlans, GetUserForPayment,
  GetUserSubscription, RunStrongSecure,
  UpdateStripeViewSettings, UpdateSubscription
} from './payment.actions';
import {PaymentStateModel} from './payment-state.model';

@State<PaymentStateModel>({
  name: 'paymentState',
  defaults: {
    userSubscription: null,
    stripePlans: [],
    selectedPlan: null,
    paymentMethods: [],
    defaultPaymentMethod: null,
    stripeViewSettings: null,
    invoices: [],
    devicesBySubscribe: 10,
    paymentIntent: null
  }
})
@Injectable()
export class PaymentState {
  constructor(
    private subscriptionService: SubscriptionService,
    private router: Router,
    private localize: LocalizeRouterService,
    private stripeService: StripeService,
    private errorService: ErrorService
  ) {
  }

  @Action(GetUserSubscription, {cancelUncompleted: true})
  getUserSubscription(ctx: StateContext<PaymentStateModel>, {type}: GetUserSubscription): Observable<void> {
    return this.subscriptionService.getStripeSubscriptions()
      .pipe(
        map(([data]) => data || null), // take firs from array, if it's not empty
        tap((userSubscription) => {
          const state = ctx.getState();

          ctx.patchState({
            userSubscription,
            devicesBySubscribe: userSubscription?.quantity ?? (state?.devicesBySubscribe ?? 10),
            selectedPlan: this.subscriptionService.findSelectedStripePlan(state?.stripePlans ?? [], userSubscription)
          });
        }),
        mergeMap(() => type === 'fullDate' ? ctx.dispatch([new GetStripeInvoice(), new GetPaymentMethods()]) : of<void>())
      );
  }

  @Action(GetStripePlans, {cancelUncompleted: true})
  getStripePlans(ctx: StateContext<PaymentStateModel>): Observable<IStripePlan[]> {
    return this.subscriptionService.getStripePlans()
      .pipe(
        tap((stripePlans) => {
          const state = ctx.getState();
          ctx.patchState({
            stripePlans,
            selectedPlan: this.subscriptionService.findSelectedStripePlan(stripePlans, state.userSubscription)
          });
        })
      );
  }

  @Action(GetPaymentMethods, {cancelUncompleted: true})
  getPaymentMethods(ctx: StateContext<PaymentStateModel>): Observable<any> {
    return this.subscriptionService.getPaymentMethods()
      .pipe(
        tap(({data}) => {
          ctx.patchState({
            paymentMethods: data
          });
        }),
        mergeMap(({data}) => data.length ? ctx.dispatch(new GetDefaultPaymentMethod()) : of(null))
      );
  }

  @Action(GetDefaultPaymentMethod, {cancelUncompleted: true})
  @Action(GetPaymentMethod, {cancelUncompleted: true})
  getPaymentMethod(ctx: StateContext<PaymentStateModel>, {paymentMethodId}: GetPaymentMethod): Observable<{ data: IPaymentMethod }> {
    return this.subscriptionService.getPaymentMethod(paymentMethodId)
      .pipe(
        tap(({data}) => {
          ctx.patchState({
            defaultPaymentMethod: data
          });
        })
      );
  }

  @Action(UpdateStripeViewSettings, {cancelUncompleted: true})
  updateStripeViewSettings(ctx: StateContext<PaymentStateModel>, {options}: UpdateStripeViewSettings): void {
    const state = ctx.getState();
    if (state.userSubscription && state.stripePlans) {

      const selectedPlan = state.stripePlans.find(plan => plan.id === state?.userSubscription?.plan?.id);
      ctx.patchState({
        selectedPlan: selectedPlan || null,
      });
    }
    ctx.patchState({
      stripeViewSettings: options || null
    });
  }

  @Action(GetStripeInvoice)
  getStripeInvoice(ctx: StateContext<PaymentStateModel>): Observable<IInvoice[]> {
    return this.subscriptionService.getStripeInvoice()
      .pipe(
        map(({data}) => data),
        tap( (invoices) => {
          ctx.patchState({
            invoices
          });
        })
      );
  }

  @Action(CreateSubscription, {cancelUncompleted: true})
  createSubscription(ctx: StateContext<PaymentStateModel>, {data}: CreateSubscription): Observable<any> {
    return this.subscriptionService
      .createSubscription(data.paymentMethodId, data.planId, data.devices)
      .pipe(
        mergeMap(() => {
          return ctx.dispatch([
            new UpdateStripeViewSettings(),
            new GetUserSubscription(),
            new GetUserForPayment()
          ]);
        }),
        catchError(err => {
          if (err.error?.data) {
            return ctx.dispatch(new RunStrongSecure(err.error.data));
          }

          this.errorService.showError(err);
          return of(null);
        })
      );
  }

  @Action(RunStrongSecure, {cancelUncompleted: true})
  runStrongSecure(ctx: StateContext<PaymentStateModel>, {intent, card}: RunStrongSecure): void {
    if (!card) {
      const path = this.localize.translateRoute(`/app/user/payment/${intent.id}`);
      this.router.navigate([path]);
      return;
    }
    this.stripeService.confirmCardPayment(intent.client_secret as string, card)
      .pipe(
        mergeMap(({paymentIntent, error}) => {
          if (error) {
            this.errorService.showError(error as any);
            return of(null);
          }
          return ctx.dispatch([
            new GetUserSubscription(),
            new GetUserForPayment(),
            new UpdateStripeViewSettings()
          ]);
        }),
        catchError(err => {
          this.errorService.showError(err);
          return of(null);
        })
      );
  }

  @Action(CreateSubscriptionIntent, {cancelUncompleted: true})
  createSubscriptionIntent(ctx: StateContext<PaymentStateModel>, {data, card}: CreateSubscriptionIntent): Observable<any> {
    return this.subscriptionService.getSetupIntent()
      .pipe(
        mergeMap((res) => this.stripeService.confirmCardSetup(res?.data.client_secret, card)),
        mergeMap(({setupIntent, error}) => {
          if (setupIntent) {
            return ctx.dispatch(new CreateSubscription({
              paymentMethodId: setupIntent.payment_method as string,
              planId: data.planId,
              devices: data.devices
            }));
          }
          else if (error) {
            this.errorService.showError(error as any);
            return of(null);
          }
          else return of(null);
        })
      );
  }

  @Action(UpdateSubscription, {cancelUncompleted: true})
  updateSubscription(ctx: StateContext<PaymentStateModel>, {subsId, data}: UpdateSubscription): Observable<any> {
    return this.subscriptionService.updateSubscription(subsId, data)
      .pipe(
        tap((res) => {
          const state = ctx.getState();
          ctx.patchState({
            userSubscription: {...state.userSubscription, ...res.data}
          });
        }),
        mergeMap(() => ctx.dispatch([
          new GetUserSubscription(),
          new UpdateStripeViewSettings(),
          new GetUserForPayment()
        ])),
        catchError((err) => {
          if (err.error?.data) {
            return ctx.dispatch(new RunStrongSecure(err.error.data));
          }

          this.errorService.showError(err);
          return of(null);
        })
      );
  }

  @Action(GetPaymentIntent, {cancelUncompleted: true})
  getStripeIntent(ctx: StateContext<PaymentStateModel>, {id}: GetPaymentIntent): Observable<any> {
    return this.subscriptionService.getPaymentIntent(id).pipe(
      tap(({data}) => {
        ctx.patchState({
           paymentIntent: data
        });
      })
    );
  }

  @Action(DeletePaymentMethod, {cancelUncompleted: true})
  deletePaymentMethod(ctx: StateContext<PaymentStateModel>, {id}: DeletePaymentMethod): Observable<any> {
    return this.subscriptionService.delPaymentMethod(id).pipe(
      tap(() => {
        const state = ctx.getState();
        ctx.patchState({
          paymentMethods: state.paymentMethods.filter(pm => pm.id !== id)
        });
      })
    );
  }

  @Action(AddPaymentMethod, {cancelUncompleted: true})
  addPaymentMethod(ctx: StateContext<PaymentStateModel>, {paymentMethodId, asDefault, card}: AddPaymentMethod): Observable<any> {
    if (paymentMethodId) {
      return this.subscriptionService.addPaymentMethod(paymentMethodId, asDefault)
        .pipe(
          mergeMap(() => ctx.dispatch([new GetDefaultPaymentMethod()]))
        );
    }
    else {
      return this.subscriptionService.getSetupIntent().pipe(
        mergeMap(({data}) => this.stripeService.confirmCardSetup(data.client_secret, card)),
        mergeMap(result => {
          if (result.setupIntent) return this.subscriptionService.addPaymentMethod(result.setupIntent.payment_method as string, asDefault);
          else if (result.error) this.errorService.showError(result as any);
          return of(null);
        }),
        mergeMap(() => ctx.dispatch([new GetPaymentMethods()]))
      );
    }
  }

  @Action(ConfirmPayment, {cancelUncompleted: true})
  confirmPayment(ctx: StateContext<PaymentStateModel>, {clientSecret, data}: ConfirmPayment): Observable<any> {
    return this.stripeService.confirmCardPayment(clientSecret, data)
      .pipe(
        mergeMap(result => {
          if (result.error) {
            this.errorService.showError(result as any);
            return of(null);
          }
          this.subscriptionService.showSuccessMsg();
          return of(null);
        }),
        catchError((err => {
          this.errorService.showError(err);
          return of(null);
        }))
      );
  }
}
